/**
 * Utility functions for handling time zone conversions in the scheduling system
 */

/**
 * Converts a time string from UTC to the user's local time zone
 *
 * @param {string} timeStr - Time string in 24-hour format (HH:MM)
 * @param {string} dayOfWeek - Day of week (monday, tuesday, etc.)
 * @returns {Object} Object containing the local time and day of week
 */
export const utcToLocalTime = (timeStr, dayOfWeek) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    const dayIndex = days.indexOf(dayOfWeek.toLowerCase());
    if (dayIndex === -1) {
        throw new Error(`Invalid day of week: ${dayOfWeek}`);
    }

    const now = new Date();
    const currentDay = now.getUTCDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysToAdd = (dayIndex - currentDay + 7) % 7;

    const date = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + daysToAdd,
        ...timeStr.split(':').map(Number)
    ));

    const localHour = date.getHours();
    const localMinute = date.getMinutes();
    const localTime = `${String(localHour).padStart(2, '0')}:${String(localMinute).padStart(2, '0')}`;

    const localDay = days[date.getDay()];

    return {
        time: localTime,
        day: localDay
    };
};

/**
 * Converts a time string from local time zone to UTC
 *
 * @param {string} timeStr - Time string in 24-hour format (HH:MM)
 * @param {string} dayOfWeek - Day of week (monday, tuesday, etc.)
 * @returns {Object} Object containing the UTC time and day of week
 */
export const localToUtcTime = (timeStr, dayOfWeek) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    const dayIndex = days.indexOf(dayOfWeek.toLowerCase());
    if (dayIndex === -1) {
        throw new Error(`Invalid day of week: ${dayOfWeek}`);
    }

    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysToAdd = (dayIndex - currentDay + 7) % 7;

    const [hour, minute] = timeStr.split(':').map(Number);
    const date = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + daysToAdd,
        hour,
        minute
    );

    const utcHour = date.getUTCHours();
    const utcMinute = date.getUTCMinutes();
    const utcTime = `${String(utcHour).padStart(2, '0')}:${String(utcMinute).padStart(2, '0')}`;

    const utcDay = days[date.getUTCDay()];

    return {
        time: utcTime,
        day: utcDay
    };
};

/**
 * Converts an entire schedule from UTC to local time
 *
 * @param {Object} utcSchedule - Schedule with times in UTC
 * @returns {Object} Schedule with times in local time zone
 */
export const convertScheduleToLocalTime = (utcSchedule) => {
    if (!utcSchedule) return {};

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const localSchedule = {};

    days.forEach(day => {
        localSchedule[day] = [];
    });

    days.forEach(day => {
        if (!utcSchedule[day]) return;

        utcSchedule[day].forEach(slot => {
            const startLocal = utcToLocalTime(slot.start, day);
            const endLocal = utcToLocalTime(slot.end, day);

            const newSlot = {
                start: startLocal.time,
                end: endLocal.time,
                label: slot.label || '',
                originalUtcDay: day,
                spansDays: startLocal.day !== endLocal.day
            };

            localSchedule[startLocal.day].push(newSlot);

            if (startLocal.day !== endLocal.day) {
                const nextDaySlot = {
                    start: '00:00',
                    end: endLocal.time,
                    label: `${slot.label || ''} (continued)`,
                    originalUtcDay: day,
                    isContinuation: true
                };

                localSchedule[endLocal.day].push(nextDaySlot);
            }
        });
    });

    days.forEach(day => {
        localSchedule[day].sort((a, b) => a.start.localeCompare(b.start));
    });

    return localSchedule;
};

/**
 * Converts an entire schedule from local time to UTC
 *
 * @param {Object} localSchedule - Schedule with times in local time zone
 * @returns {Object} Schedule with times in UTC
 */
export const convertScheduleToUtcTime = (localSchedule) => {
    if (!localSchedule) return {};

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const utcSchedule = {};

    days.forEach(day => {
        utcSchedule[day] = [];
    });

    days.forEach(day => {
        if (!localSchedule[day]) return;

        localSchedule[day].forEach(slot => {
            if (slot.isContinuation) return;

            const startUtc = localToUtcTime(slot.start, day);
            let endDay = day;

            if (slot.spansDays) {
                const dayIndex = days.indexOf(day);
                const nextDayIndex = (dayIndex + 1) % 7;
                endDay = days[nextDayIndex];
            }

            const endUtc = localToUtcTime(slot.end, endDay);

            const newSlot = {
                start: startUtc.time,
                end: endUtc.time,
                label: slot.label?.replace(' (continued)', '') || ''
            };

            utcSchedule[startUtc.day].push(newSlot);
        });
    });

    days.forEach(day => {
        utcSchedule[day].sort((a, b) => a.start.localeCompare(b.start));
    });

    return utcSchedule;
};

/**
 * Gets the user's time zone display name
 *
 * @returns {string} User's time zone name and offset
 */
export const getUserTimeZoneDisplay = () => {
    const now = new Date();
    const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const offset = -now.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offset / 60));
    const offsetMinutes = Math.abs(offset % 60);
    const offsetSign = offset >= 0 ? '+' : '-';
    const offsetFormatted = `UTC${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

    return `${timeZoneName} (${offsetFormatted})`;
};